import { forwardRef, useEffect, useId, useRef, type InputHTMLAttributes, type ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Checkbox>. Markup
// (incl. the indeterminate DOM-property wiring, which has no HTML
// attribute equivalent) and CSS ported letter-for-letter from the
// vendor's compiled bundle and its Checkbox CSS — new .rc-checkbox class
// names. Check icon switched from @datasirpi/icons to lucide-react, same
// reasoning as Banner (design.md specifies Lucide; already a dependency).
export type CheckboxSize = "sm" | "md";

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "onChange"> {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: CheckboxSize;
  indeterminate?: boolean;
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, error, size = "md", indeterminate = false, disabled, className, id: providedId, onChange, ...rest },
  ref
) {
  const fallbackId = useId();
  const inputId = providedId ?? fallbackId;
  const descId = description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;
  const describedBy = [descId, errorId].filter(Boolean).join(" ") || undefined;

  const innerRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  function setRef(node: HTMLInputElement | null) {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }

  return (
    <label
      htmlFor={inputId}
      className={cn("rc-checkbox", `rc-checkbox--${size}`, disabled && "rc-checkbox--disabled", error && "rc-checkbox--error", className)}
    >
      <input
        ref={setRef}
        id={inputId}
        type="checkbox"
        className="rc-checkbox__input"
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange?.(e.target.checked, e)}
        {...rest}
      />
      <span className="rc-checkbox__box" aria-hidden="true">
        {indeterminate ? <span className="rc-checkbox__bar" /> : <Check />}
      </span>
      <span className="rc-checkbox__text">
        <span className="rc-checkbox__label">{label}</span>
        {description ? (
          <span id={descId} className="rc-checkbox__description">
            {description}
          </span>
        ) : null}
        {error ? (
          <span id={errorId} className="rc-checkbox__error" role="alert">
            {error}
          </span>
        ) : null}
      </span>
    </label>
  );
});
